/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import { type CSSProperties, type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import styles from "./RaisedHandsOverlay.module.css";
import { type CallViewModel } from "../state/CallViewModel";
import { useBehavior } from "../useBehavior";

/**
 * Форматирует ожидание как «0:42» / «12:05» / «1:02:30». Отдельная функция, а не
 * Intl.DurationFormat: тот в цифровом стиле дополняет минуты нулём («00:42»), а
 * нам нужен привычный вид секундомера.
 */
export function formatRaisedHandDuration(elapsedMs: number): string {
  const totalSeconds = Math.max(0, Math.floor(elapsedMs / 1000));
  const seconds = totalSeconds % 60;
  const minutes = Math.floor(totalSeconds / 60) % 60;
  const hours = Math.floor(totalSeconds / 3600);
  const paddedSeconds = String(seconds).padStart(2, "0");
  return hours > 0
    ? `${hours}:${String(minutes).padStart(2, "0")}:${paddedSeconds}`
    : `${minutes}:${paddedSeconds}`;
}

interface Props {
  vm: CallViewModel;
  style?: CSSProperties;
}

/**
 * Список поднятых рук поверх демонстрации экрана. Во время демонстрации плитки
 * участников уезжают в узкую полосу или прячутся совсем, и значок руки на плитке
 * докладчик уже не замечает.
 */
export function RaisedHandsOverlay({ vm, style }: Props): ReactNode {
  const { t } = useTranslation();
  const screenShareActive = useBehavior(vm.screenShareActive$);
  const raisedHands = useBehavior(vm.raisedHandsQueue$);
  // Состояние нужно только чтобы раз в секунду перерисовать список; само время
  // берём при отрисовке, иначе после паузы таймера показалось бы устаревшее
  const [, setTick] = useState(0);

  const visible = screenShareActive && raisedHands.length > 0;

  useEffect(() => {
    if (!visible) return;
    const interval = setInterval(() => setTick((tick) => tick + 1), 1000);
    return (): void => clearInterval(interval);
  }, [visible]);

  if (!visible) return null;

  const now = Date.now();

  return (
    <div
      className={styles.overlay}
      style={style}
      role="status"
      aria-live="polite"
      data-testid="raised_hands_overlay"
    >
      <div className={styles.title}>
        <span aria-hidden>✋</span>
        {t("raised_hands_overlay.title")}
      </div>
      <ol className={styles.list}>
        {raisedHands.map(({ identifier, displayName, time }) => (
          <li key={identifier} className={styles.row}>
            <span className={styles.name}>{displayName}</span>
            <span className={styles.separator} aria-hidden>
              ·
            </span>
            {/* Секундомер тикает каждую секунду: без aria-live="off" экранный
                диктор зачитывал бы строку заново на каждом такте */}
            <span className={styles.duration} aria-live="off">
              {formatRaisedHandDuration(now - time.getTime())}
            </span>
          </li>
        ))}
      </ol>
    </div>
  );
}
